import React from "react";
import { isNonEmptyArray } from "diagnostics-lib/array";
import Loading from "@ui/Loading";
import BlankStateMeasures from "../../../../../../Measures/components/BlankStateMeasures";
import QuantityLayout from "../../../../../../Chart/QuantityLayout";
import { QuantitySelectorType } from "../../../../../../QuantitySelector";
import QuantitySelector from "../../../FaultEvent/components/Tabs/components/QuantitySelector";
import { useAllAnalysesMode } from "../../../../../../useAllAnalysesMode";
import useAnalyses from "../../../../../../../../api/useAnalyses";
import useSelectedAnalyses from "../../../../../../Analysis/useSelectedAnalyses";
import { useMeasureChartType } from "../../../../../../Measures/hook/useMeasureChartType";
import type { Project } from "../../../../../../../../api/useProjects";
import { useProjectSource, type Source } from "../../../../../../../../api/useSources";
import type { SelectedQuantities } from "../../../../../../useSelectedQuantities";
import useSelectedQuantities from "../../../../../../useSelectedQuantities";
import MeasuresEvolution from "./MeasuresEvolution";
import { css } from "@linaria/core";
import {
  FAULT_EVENT_HEADER_HEIGHT_CSS_VARIABLE,
  FAULT_EVENT_NAVIGATION_HEIGHT_CSS_VARIABLE,
  PRIMARY_HEADER_HEIGHT_CSS_VARIABLE,
} from "@ui/Header/useHeaderHeight";
import { useOutletContext } from "react-router";
import type { Event } from "../../../../../../../../api/useEvents";
import { getEventScope } from "../../../../../../../../domain/event";
import { useEventPeriod } from "../../../../../hooks/useEventPeriod";
import type { EventOutletContext } from "../../..";
import useActiveAnalysesWithDisplayFilters from "../../../../../../../../api/useActiveAnalysesWithDisplayFilters";
import useActiveAnalysesStream from "../../../../../../../../api/useActiveAnalysesStream";

interface Props {
  project: Project;
  event: Event;
  source: Source;
  selectedQuantities: SelectedQuantities;
}

const ProjectMeasures = ({ project, event, selectedQuantities, source }: Props) => {
  const [_, setSelectedQuantities] = useSelectedQuantities(project.id);
  const { activeAnalyses } = useActiveAnalysesWithDisplayFilters(project.id);
  const { activeAnalyses: rawActiveAnalyses } = useActiveAnalysesStream(project.id);
  const [_allAnalysesMode, setAllAnalysesMode] = useAllAnalysesMode({ scope: project.id });
  const allAnalysesMode = rawActiveAnalyses.length === 0 || _allAnalysesMode;
  const activeAnalysesForMeasures = allAnalysesMode ? rawActiveAnalyses : activeAnalyses;
  const { result: analyses, samplingDescription } = useAnalyses(project, source, allAnalysesMode);
  const { selectedAnalyses } = useSelectedAnalyses(project.id, activeAnalysesForMeasures);

  const [period] = useEventPeriod(
    allAnalysesMode
      ? { event, source, project, boundaries: "analyses" }
      : { event, source, project, analyses: activeAnalyses, boundaries: "active-analyses" },
  );

  const [selectedChartType, setSelectedChartOption] = useMeasureChartType({
    scope: getEventScope(event),
    defaultValue: "value",
  });

  const evolution = React.useMemo(
    () => (
      <MeasuresEvolution
        samplingDescription={samplingDescription}
        project={project}
        source={source}
        analyses={analyses}
        activeAnalyses={activeAnalysesForMeasures}
        selectedQuantities={selectedQuantities}
        setSelectedChartOption={setSelectedChartOption}
        selectedChartOption={selectedChartType}
        allAnalysesMode={allAnalysesMode}
        setAllAnalysesMode={setAllAnalysesMode}
        event={event}
        rawActiveAnalysesCount={rawActiveAnalyses.length}
      />
    ),
    [
      activeAnalysesForMeasures,
      allAnalysesMode,
      analyses,
      event,
      project,
      samplingDescription,
      selectedChartType,
      selectedQuantities,
      setAllAnalysesMode,
      setSelectedChartOption,
      source,
      rawActiveAnalyses.length,
    ],
  );

  const quantities = React.useMemo(
    () => (
      <QuantitySelector
        event={event}
        project={project}
        onlyFirstDisplayed={false}
        selectedQuantities={selectedQuantities}
        setSelectedQuantities={setSelectedQuantities}
        activeAnalyses={activeAnalysesForMeasures}
        selectedAnalyses={selectedAnalyses}
        alertType={QuantitySelectorType.Measures}
        period={period}
        allAnalysesMode={allAnalysesMode}
        page={QuantitySelectorType.Measures}
      />
    ),
    [
      event,
      activeAnalysesForMeasures,
      period,
      project,
      selectedAnalyses,
      selectedQuantities,
      setSelectedQuantities,
      allAnalysesMode,
    ],
  );

  return (
    <QuantityLayout className={styledQuantityLayout} quantities={quantities} compact>
      {evolution}
    </QuantityLayout>
  );
};

export const DataTab = () => {
  const { project, event } = useOutletContext<EventOutletContext>();

  if (!project) throw new Error("no project");
  if (!event) throw new Error("no event");

  const [selectedQuantities] = useSelectedQuantities(project.id);
  const source = useProjectSource(project);
  if (source === null) return <Loading />;

  if (!isNonEmptyArray(selectedQuantities)) return <BlankStateMeasures />;

  return (
    <ProjectMeasures
      event={event}
      project={project}
      source={source}
      selectedQuantities={selectedQuantities}
    />
  );
};

//#region CSS
const styledQuantityLayout = css`
  --dyn-min-height: calc(
    100vh - var(${PRIMARY_HEADER_HEIGHT_CSS_VARIABLE}) - var(
        ${FAULT_EVENT_HEADER_HEIGHT_CSS_VARIABLE}
      ) - var(${FAULT_EVENT_NAVIGATION_HEIGHT_CSS_VARIABLE})
  );
  min-height: max(var(--dyn-min-height), 650px);
`;
//#endregion CSS
